import { noop } from 'lodash';
import { ReactNode } from 'react';
import { PathParam, generatePath } from 'react-router-dom';

import CommissionedIcon from 'assets/volvoIcons/Status/CommissionedIcon';
import ConsumerRegisteredIcon from 'assets/volvoIcons/Status/ConsumerRegisteredIcon';
import DemoUseRegisteredIcon from 'assets/volvoIcons/Status/DemoUseRegisteredIcon';
import InternallyInvoicedIcon from 'assets/volvoIcons/Status/InternallyInvoicedIcon';
import InvoicedIcon from 'assets/volvoIcons/Status/InvoicedIcon';
import ManufacturedIcon from 'assets/volvoIcons/Status/ManufacturedIcon';
import ProductInOperationIcon from 'assets/volvoIcons/Status/ProductInOperationIcon';
import RegisteredUnitIcon from 'assets/volvoIcons/Status/RegisteredUnitIcon';
import ReplacedIcon from 'assets/volvoIcons/Status/ReplacedIcon';
import ScrappedIcon from 'assets/volvoIcons/Status/ScrappedIcon';
import { ParseKeys, TOptions } from 'i18next';
import {
	UnitOverviewPageUrl,
	UnitOverview_Configuration,
	UnitOverview_EndCustomer_Url,
	productIndividualEventLog,
	productIndividualLogisticTrail,
	productIndividualviewPageUrl,
	unitCommissioning,
	unitOverview_DemoDealerUsage_Url,
	unitProductInOperation
} from 'main/routes/urls/RouteUrls';

export type ProductIndividualStatus =
	| 'Manufactured'
	| 'InternallyInvoiced'
	| 'Invoiced'
	| 'Replaced'
	| 'Scrapped';

export type UnitStatus =
	| 'RegisteredUnit'
	| 'DemoUseRegistered'
	| 'ConsumerRegistered'
	| 'ProductInOperation'
	| 'Commissioned';

export type Status = ProductIndividualStatus | UnitStatus;

export const productIndividualStatusTranslationMapping: {
	[key in ProductIndividualStatus]: ParseKeys<
		['commonStatus'],
		TOptions,
		'commonStatus'
	>;
} = {
	Manufactured: 'commonStatus:manufactured',
	InternallyInvoiced: 'commonStatus:internally-invoiced',
	Invoiced: 'commonStatus:invoiced',
	Replaced: 'commonStatus:replaced',
	Scrapped: 'commonStatus:scrapped',
};

export const unitStatusTranslationMapping: {
	[key in UnitStatus]: ParseKeys<['commonStatus'], TOptions, 'commonStatus'>;
} = {
	RegisteredUnit: 'commonStatus:unit-created',
	DemoUseRegistered: 'commonStatus:demo-registered',
	ConsumerRegistered: 'commonStatus:customer-added',
	ProductInOperation: 'commonStatus:product-in-operation',
	Commissioned: 'commonStatus:commissioned',
};

export const statusTranslationMapping: {
	[key in Status | 'Null']: ParseKeys<
		['commonStatus'],
		TOptions,
		'commonStatus'
	>;
} = {
	Null: 'commonStatus:unknown',
	...productIndividualStatusTranslationMapping,
	...unitStatusTranslationMapping,
};

export const productIndividualStatusIconMapping: {
	[key in ProductIndividualStatus]: ReactNode;
} = {
	Manufactured: <ManufacturedIcon />,
	InternallyInvoiced: <InternallyInvoicedIcon />,
	Invoiced: <InvoicedIcon />,
	Replaced: <ReplacedIcon />,
	Scrapped: <ScrappedIcon />,
};

export const unitStatusIconMapping: { [key in UnitStatus]: ReactNode } = {
	RegisteredUnit: <RegisteredUnitIcon />,
	DemoUseRegistered: <DemoUseRegisteredIcon />,
	ConsumerRegistered: <ConsumerRegisteredIcon />,
	ProductInOperation: <ProductInOperationIcon />,
	Commissioned: <CommissionedIcon />,
};

export const statusIconMapping = {
	...productIndividualStatusIconMapping,
	...unitStatusIconMapping,
};

export const statusToUrlMapping: {
	[key in Status]: string;
} = {
	Manufactured: productIndividualviewPageUrl,
	InternallyInvoiced: productIndividualviewPageUrl,
	Invoiced: productIndividualviewPageUrl,
	Replaced: productIndividualviewPageUrl,
	Scrapped: productIndividualviewPageUrl,
	RegisteredUnit: `${UnitOverviewPageUrl}/${UnitOverview_Configuration}`,
	DemoUseRegistered: unitOverview_DemoDealerUsage_Url,
	ConsumerRegistered: UnitOverview_EndCustomer_Url,
	ProductInOperation: `${UnitOverviewPageUrl}/${unitProductInOperation}`,
	Commissioned: `${UnitOverviewPageUrl}/${unitCommissioning}`,
};

const getProductIndividualUrlParam =
	(section: string) => (serialnumber: string) => ({
		serialnumber,
		section,
	});

const getUnitIdUrlParam = (unitId: string) => ({
	unitId,
});

const getStatusToUrlParams: {
	[key in Status]: (id: string) => {
		[key in PathParam<string>]: string | null;
	};
} = {
	Manufactured: getProductIndividualUrlParam(productIndividualLogisticTrail),
	InternallyInvoiced: getProductIndividualUrlParam(
		productIndividualLogisticTrail
	),
	Invoiced: getProductIndividualUrlParam(productIndividualLogisticTrail),
	Replaced: getProductIndividualUrlParam(productIndividualEventLog),
	Scrapped: getProductIndividualUrlParam(productIndividualEventLog),
	RegisteredUnit: getUnitIdUrlParam,
	DemoUseRegistered: getUnitIdUrlParam,
	ConsumerRegistered: getUnitIdUrlParam,
	ProductInOperation: getUnitIdUrlParam,
	Commissioned: getUnitIdUrlParam,
};

export const getStatusUrl = (status: Status, id: string) =>
	generatePath(
		statusToUrlMapping[status],
		(getStatusToUrlParams[status] || noop)(id)
	);

export const DEMO_DEALER_STATUS_LIST: (Status | null)[] = [
	'RegisteredUnit',
	'DemoUseRegistered',
];

export const WARRANTY_STARTED_STATUS_LIST: (Status | null)[] = [
	'Commissioned',
	'ProductInOperation',
	'DemoUseRegistered',
];

export const CONSUMER_REGISTER_ALLOWED_STATUS_LIST: (Status | null)[] = [
	'RegisteredUnit',
	'DemoUseRegistered',
];

export const DEMO_REGISTER_ALLOWED_STATUS_LIST: (Status | null)[] = [
	'RegisteredUnit',
];

export const PRODUCT_IN_OPERATION_ALLOWED_STATUS_LIST: (Status | null)[] = [
	'ConsumerRegistered',
];

export const END_CUSTOMER_AVAILABLE_STATUS_LIST: (Status | null)[] = [
	'ConsumerRegistered',
	'ProductInOperation',
	'Commissioned',
];

export const PRODUCT_IN_OPERATION_AVAILABLE_STATUS_LIST: (Status | null)[] = [
	'ProductInOperation',
	'Commissioned',
];

export const COMMISSIONING_ALLOWED_STATUS_LIST: (Status | null)[] = [
	'ProductInOperation',
];

export const COMMISSIONING_AVAILABLE_STATUS_LIST: (Status | null)[] = [
	'Commissioned',
];

export const RELOCATE_AVAILABLE_STATUS_LIST: (Status | null)[] = [
	'RegisteredUnit',
	'ConsumerRegistered',
	'DemoUseRegistered',
];

export const SCRAP_STATUS_LIST: (Status | null)[] = [
	'Invoiced',
	'InternallyInvoiced',
	'Manufactured',
	'Replaced',
];

export const RESET_STATUS_LIST: (Status | null)[] = ['Scrapped', 'Replaced'];
